import React, { createContext, useContext, useState, useEffect } from 'react';

const FormPopupContext = createContext();

export const useFormPopup = () => {
  const context = useContext(FormPopupContext);
  if (!context) {
    throw new Error('useFormPopup must be used within FormPopupProvider');
  }
  return context;
};

export const FormPopupProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formSlug, setFormSlug] = useState(null);

  const openFormPopup = (slug) => {
    setFormSlug(slug);
    setIsOpen(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeFormPopup = () => {
    setIsOpen(false);
    setFormSlug(null);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  // Listen for data-form-popup attribute clicks
  useEffect(() => {
    const handleDataAttributeClick = (e) => {
      const button = e.target.closest('[data-form-popup]');
      if (button) {
        e.preventDefault();
        const slug = button.getAttribute('data-form-popup');
        if (slug) {
          openFormPopup(slug);
        }
      }
    };

    document.addEventListener('click', handleDataAttributeClick);
    
    return () => {
      document.removeEventListener('click', handleDataAttributeClick);
    };
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeFormPopup();
      }
    };

    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const value = {
    isOpen,
    formSlug,
    openFormPopup,
    closeFormPopup
  };

  return (
    <FormPopupContext.Provider value={value}>
      {children}
    </FormPopupContext.Provider>
  );
};

// Global function for easy access from anywhere
if (typeof window !== 'undefined') {
  window.openFormPopup = null; // Will be set by FormPopupModal
}
